/**
 * structure_solids — a lightweight, physics-free spatial registry of the SOLID
 * volumes of streamed settlement buildings.
 *
 * Ship-flight collision and cannon-bolt hit-tests need to ask "is (x,z,y) inside
 * a building, and where is its surface?" at ANY altitude. The physics world is
 * unusable for this in flight: it only steps inside PhysicsModule.moveAvatar(),
 * which is parked while the avatar rides a ship, so colliders streamed during
 * flight never become query-visible. And the plan does not carry a usable roof
 * height (the renderer derives it from civTargetHeight + a capped fit-scale).
 *
 * So CommonsSettlementsModule registers each colliding building here at build
 * time from its ACTUAL world transform + scaled bounding box (base → roof), and
 * clears the site's solids on dispose. Buildings are boxes rotated about Y; the
 * registry bins them on a coarse grid so a query stays O(a few) even with several
 * cities resident. Everything lives in the same local/scene space the flight loop
 * and bolts use (site.centerX ↔ atlas.localX), so no transform is needed.
 */

export interface StructureSolid {
  /** Footprint center, world (== local/scene) space. */
  x: number;
  z: number;
  /** Footprint half-extents along the building's own X/Z (pre-yaw). */
  hx: number;
  hz: number;
  /** Footprint rotation about Y. */
  yaw: number;
  /** World Y span: ground (base) → roof (top). */
  base: number;
  top: number;
  /** A ladder band of a prefab the ground-level MASK (lib/structure_masks.ts) also knows: at walking
   *  height the mask answers for it, so a hull on the ground skips it where the mask covers (2026-09-23). */
  masked?: boolean;
}

const BIN = 128; // m — a building footprint (≤ ~60 m half-extent) touches 1–2 cells
const bins = new Map<string, StructureSolid[]>();
const bySite = new Map<number, StructureSolid[]>();

/** The query margin structureHitAt is ever asked for (SHIP_HALF_W = 6) — the bin reach must cover it. */
const MAX_QUERY_MARGIN = 8;

function cellKeysFor(s: StructureSolid): string[] {
  // Conservative (yaw-agnostic) reach: a rotated rect reaches hypot(hx, hz) from its centre (its
  // CORNER), never max(hx, hz) — and structureHitAt looks in ONE bin with a margin up to 6 m, so
  // the reach carries that too. WHY (2026-09-23): a 40×10 m hall at 45° registered 21 m out but
  // its corners stood 41 m out; a ship crossing the bin line hit nothing until the centre bin.
  const r = Math.hypot(s.hx, s.hz) + MAX_QUERY_MARGIN;
  const x0 = Math.floor((s.x - r) / BIN), x1 = Math.floor((s.x + r) / BIN);
  const z0 = Math.floor((s.z - r) / BIN), z1 = Math.floor((s.z + r) / BIN);
  const keys: string[] = [];
  for (let cx = x0; cx <= x1; cx++) for (let cz = z0; cz <= z1; cz++) keys.push(`${cx},${cz}`);
  return keys;
}

/** Every solid whose footprint reach touches a circle of `r` around (x, z) — rooftop perches for the birds (2026-09-11). */
export function structureSolidsNear(x: number, z: number, r: number): StructureSolid[] {
  const out: StructureSolid[] = [];
  const seen = new Set<StructureSolid>();
  const x0 = Math.floor((x - r) / BIN), x1 = Math.floor((x + r) / BIN);
  const z0 = Math.floor((z - r) / BIN), z1 = Math.floor((z + r) / BIN);
  for (let cx = x0; cx <= x1; cx++) for (let cz = z0; cz <= z1; cz++) {
    const arr = bins.get(`${cx},${cz}`);
    if (!arr) continue;
    for (const s of arr) {
      if (seen.has(s)) continue;
      seen.add(s);
      // The same corner reach as cellKeysFor: max(hx, hz) dropped every diagonal footprint whose corner was within r.
      if (Math.hypot(s.x - x, s.z - z) <= r + Math.hypot(s.hx, s.hz)) out.push(s);
    }
  }
  return out;
}

/**
 * THE CHANGE LOG (2026-10-02, the ground keep-out — lib/ground_keepout.ts): every add and clear bumps a version and
 * records the rect it touched, so what grows on the ground can clear itself from under a building that assembled
 * after it was planted, and grow back where one was taken down. Bounded; a reader past its reach gets null.
 */
export interface SolidsChange { x0: number; z0: number; x1: number; z1: number; removed: boolean }
const CHANGE_MAX = 16384;
let solidsVersion = 0;
const changes: Array<SolidsChange & { v: number }> = [];
function noteSolidsChange(x0: number, z0: number, x1: number, z1: number, removed: boolean): void {
  solidsVersion++;
  changes.push({ v: solidsVersion, x0, z0, x1, z1, removed });
  if (changes.length > CHANGE_MAX) changes.splice(0, changes.length - CHANGE_MAX);
}
/** The registry's version: it moves on every add and every clear. */
export function structureSolidsVersion(): number { return solidsVersion; }
/** The rects changed after version `v` (null when the log no longer reaches back that far). */
export function structureSolidsChangesSince(v: number): SolidsChange[] | null {
  if (v >= solidsVersion) return [];
  if (changes.length === 0 || changes[0].v > v + 1) return null;
  const out: SolidsChange[] = [];
  for (const c of changes) if (c.v > v) out.push({ x0: c.x0, z0: c.z0, x1: c.x1, z1: c.z1, removed: c.removed });
  return out;
}

/** Register one building's solid volume under a settlement id. */
export function addStructureSolid(siteId: number, s: StructureSolid): void {
  const reach = Math.hypot(s.hx, s.hz);
  noteSolidsChange(s.x - reach, s.z - reach, s.x + reach, s.z + reach, false);
  for (const k of cellKeysFor(s)) {
    let arr = bins.get(k);
    if (!arr) { arr = []; bins.set(k, arr); }
    arr.push(s);
  }
  let site = bySite.get(siteId);
  if (!site) { site = []; bySite.set(siteId, site); }
  site.push(s);
}

/** Drop every solid a settlement registered (called on disposeSettlement). */
export function clearStructureSolids(siteId: number): void {
  const arr = bySite.get(siteId);
  if (!arr) return;
  bySite.delete(siteId);
  for (const s of arr) { const reach = Math.hypot(s.hx, s.hz); noteSolidsChange(s.x - reach, s.z - reach, s.x + reach, s.z + reach, true); }
  const dead = new Set(arr);
  const touched = new Set<string>();
  for (const s of arr) for (const k of cellKeysFor(s)) touched.add(k);
  for (const k of touched) {
    const b = bins.get(k);
    if (!b) continue;
    const kept = b.filter((s) => !dead.has(s));
    if (kept.length) bins.set(k, kept); else bins.delete(k);
  }
}

/** Transform a world XZ into a solid's local (yaw-aligned) footprint frame.
 *  THE YAW IS THREE'S rotation about Y (every producer composes its instance with it:
 *  local x → (cos yaw, −sin yaw), local z → (sin yaw, cos yaw)). The first version
 *  inverted a standard 2D rotation instead — a MIRRORED frame that put every diagonal
 *  footprint's hit box at twice its angle (2026-09-16, found by the circuit barriers;
 *  axis-aligned boxes never showed it). */
function toLocal(s: StructureSolid, wx: number, wz: number): { lx: number; lz: number } {
  const dx = wx - s.x, dz = wz - s.z;
  const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
  return { lx: dx * c - dz * sn, lz: dx * sn + dz * c };
}

/**
 * The tallest building solid that contains (wx, wz, y), or null. `margin` grows
 * the footprint so a ship (or bolt) registers a hit slightly before visual
 * overlap. Vertical span is [base, top]: a point above the roof is NOT a hit
 * (you may fly over), a point below the base is underground (terrain owns it).
 * `unmaskedOnly` skips the solids a ground-level mask answers for (hullBlockedAt,
 * inside a mask site at walking height: the mask said "free" — only a building the
 * mask does not know may still block).
 */
export function structureHitAt(wx: number, wz: number, y: number, margin = 0, unmaskedOnly = false): StructureSolid | null {
  const arr = bins.get(`${Math.floor(wx / BIN)},${Math.floor(wz / BIN)}`);
  if (!arr) return null;
  let best: StructureSolid | null = null;
  for (const s of arr) {
    if (unmaskedOnly && s.masked) continue;
    if (y < s.base || y > s.top + margin) continue;
    const { lx, lz } = toLocal(s, wx, wz);
    if (Math.abs(lx) <= s.hx + margin && Math.abs(lz) <= s.hz + margin) {
      if (!best || s.top > best.top) best = s;
    }
  }
  return best;
}

/**
 * Eject a point to just outside a solid's footprint along the axis of LEAST
 * penetration (the nearest face) — the flight collision push-out. Returns the
 * new world XZ. If the point is dead-center (no penetration axis), it is pushed
 * along +local-X as a degenerate fallback.
 */
export function structurePushOut(s: StructureSolid, wx: number, wz: number, margin = 4): { x: number; z: number } {
  let { lx, lz } = toLocal(s, wx, wz);
  const penX = s.hx + margin - Math.abs(lx);
  const penZ = s.hz + margin - Math.abs(lz);
  if (penX <= penZ) lx = (lx >= 0 ? 1 : -1) * (s.hx + margin);
  else lz = (lz >= 0 ? 1 : -1) * (s.hz + margin);
  // local → world (three's rotation about Y, then translate).
  const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
  return { x: s.x + lx * c + lz * sn, z: s.z - lx * sn + lz * c };
}

/** Diagnostic: total registered solids across all resident settlements. */
export function structureSolidCount(): number {
  let n = 0;
  for (const arr of bySite.values()) n += arr.length;
  return n;
}

/** Test-only: wipe the registry (harness fixtures). */
export function __resetStructureSolids(): void {
  bins.clear();
  bySite.clear();
  noteSolidsChange(-1e9, -1e9, 1e9, 1e9, true);
}
